import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  MenuItem,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  MoneyTransactionFragmentFragment,
  CreateMoneyTransactionDocument,
  CreateMoneyTransactionMutation,
  CreateMoneyTransactionMutationVariables,
  UpdateMoneyTransactionDocument,
  UpdateMoneyTransactionMutation,
  UpdateMoneyTransactionMutationVariables,
  DeleteMoneyTransactionDocument,
  DeleteMoneyTransactionMutation,
  DeleteMoneyTransactionMutationVariables,
  GetAccountByIdDocument,
  GetAccountByIdQuery,
  GetAccountByIdQueryVariables,
  DeleteInvoicePaymentDocument,
  DeleteInvoicePaymentMutation,
  DeleteInvoicePaymentMutationVariables,
  CreateInvoicePaymentDocument,
  CreateInvoicePaymentMutation,
  CreateInvoicePaymentMutationVariables,
  CreateExpensePaymentDocument,
  CreateExpensePaymentMutation,
  CreateExpensePaymentMutationVariables,
  DeleteExpensePaymentDocument,
  DeleteExpensePaymentMutation,
  DeleteExpensePaymentMutationVariables,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useAuth } from '@dwelvio/auth-react';
import { IUserData } from '../../../types';
import { InvoicePaymentList } from '../../../components/invoice-payment-list';
import { ExpensePaymentList } from '../../../components/expense-payment-list';
import { ConfirmationDialog } from '../../../components/confirmation-dialog';

interface FormValues {
  transactionDate: string;
  type: 'DEBIT' | 'CREDIT';
  value: string;
  cash: boolean;
  bankAccountNo: string;
  otherAccountNo: string;
  note: string;
  reference: string;
}

interface MoneyTransactionDialogProps {
  open: boolean;
  transaction: MoneyTransactionFragmentFragment | null;
  onClose: () => void;
  onRefresh?: () => void;
  onAssignPayment?: () => void;
  /**
   * Optional invoice assignment configuration.
   * When provided, the dialog will automatically assign the created transaction to this invoice.
   */
  invoiceAssignment?: {
    invoiceId: string;
    invoiceAmount: number;
    paidAmount: number;
  };
  /**
   * Optional expense assignment configuration.
   * When provided, the dialog will automatically assign the created transaction to this expense.
   */
  expenseAssignment?: {
    expenseId: string;
    expenseAmount: number;
    paidAmount: number;
  };
}

export function MoneyTransactionDialog({
  open,
  transaction,
  onClose,
  onRefresh,
  onAssignPayment,
  invoiceAssignment,
  expenseAssignment,
}: MoneyTransactionDialogProps) {
  const { show } = useAlert();
  const { user } = useAuth<IUserData>();
  const isEdit = !!transaction;

  const { register, control, handleSubmit, watch, reset } = useForm<FormValues>({
    defaultValues: {
      transactionDate: new Date().toISOString().substring(0, 10),
      type: 'CREDIT',
      value: '',
      cash: false,
      bankAccountNo: '',
      otherAccountNo: '',
      note: '',
      reference: '',
    },
  });

  const watchCash = watch('cash');
  const watchValue = watch('value');

  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [assignmentToDeleteType, setAssignmentToDeleteType] = useState<'invoice' | 'expense'>('invoice');
  const [showOverpaymentConfirm, setShowOverpaymentConfirm] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<{ data: FormValues; transactionValue: number } | null>(null);

  const { data: accountData } = useQuery<GetAccountByIdQuery, GetAccountByIdQueryVariables>(GetAccountByIdDocument, {
    variables: { id: user?.accountId },
    skip: !user?.accountId,
  });

  const account = accountData?.getAccountById;
  const defaultBankAccountNo = account?.bankAccountNo || '';

  const [createTransaction, { loading: creating }] = useMutation<
    CreateMoneyTransactionMutation,
    CreateMoneyTransactionMutationVariables
  >(CreateMoneyTransactionDocument);
  const [updateTransaction, { loading: updating }] = useMutation<
    UpdateMoneyTransactionMutation,
    UpdateMoneyTransactionMutationVariables
  >(UpdateMoneyTransactionDocument);
  const [deleteTransaction, { loading: deleting }] = useMutation<
    DeleteMoneyTransactionMutation,
    DeleteMoneyTransactionMutationVariables
  >(DeleteMoneyTransactionDocument);

  const [createInvoicePayment, { loading: creatingInvoicePayment }] = useMutation<
    CreateInvoicePaymentMutation,
    CreateInvoicePaymentMutationVariables
  >(CreateInvoicePaymentDocument);

  const [createExpensePayment, { loading: creatingExpensePayment }] = useMutation<
    CreateExpensePaymentMutation,
    CreateExpensePaymentMutationVariables
  >(CreateExpensePaymentDocument);

  const [deleteInvoicePayment] = useMutation<DeleteInvoicePaymentMutation, DeleteInvoicePaymentMutationVariables>(
    DeleteInvoicePaymentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa odstrániť úhradu.', e.message);
      },
      onCompleted: () => {
        show.info('Úhrada bola odstránená.');
      },
    },
  );

  const [deleteExpensePayment] = useMutation<DeleteExpensePaymentMutation, DeleteExpensePaymentMutationVariables>(
    DeleteExpensePaymentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa odstrániť úhradu.', e.message);
      },
      onCompleted: () => {
        show.info('Úhrada bola odstránená.');
      },
    },
  );

  useEffect(() => {
    if (transaction) {
      reset({
        transactionDate: new Date(transaction.transactionDate).toISOString().substring(0, 10),
        type: transaction.type,
        value: transaction.value.toString(),
        cash: transaction.cash,
        bankAccountNo: transaction.bankAccountNo,
        otherAccountNo: transaction.otherAccountNo || '',
        note: transaction.note || '',
        reference: transaction.reference || '',
      });
    } else {
      // Reset form for new transaction
      const remainingAmount = invoiceAssignment
        ? invoiceAssignment.invoiceAmount - invoiceAssignment.paidAmount
        : expenseAssignment
          ? expenseAssignment.expenseAmount - expenseAssignment.paidAmount
          : 0;
      reset({
        transactionDate: new Date().toISOString().substring(0, 10),
        type: expenseAssignment ? 'DEBIT' : 'CREDIT',
        value: remainingAmount > 0 ? remainingAmount.toFixed(2) : '',
        cash: false,
        bankAccountNo: defaultBankAccountNo,
        otherAccountNo: '',
        note: '',
        reference: '',
      });
    }
  }, [transaction, defaultBankAccountNo, invoiceAssignment, expenseAssignment, open, reset]);

  const handleSave = async (data: FormValues) => {
    if (!data.transactionDate || !data.value) {
      show.error('Vyplňte všetky povinné polia.');
      return;
    }

    if (!data.cash && !data.bankAccountNo) {
      show.error('Vyplňte číslo bankového účtu.');
      return;
    }

    const transactionValue = parseFloat(data.value);

    // Check for overpayment if invoice assignment is provided
    if (invoiceAssignment && !isEdit) {
      const remainingAmount = invoiceAssignment.invoiceAmount - invoiceAssignment.paidAmount;
      if (transactionValue > remainingAmount && remainingAmount > 0) {
        setPendingFormData({ data, transactionValue });
        setShowOverpaymentConfirm(true);
        return;
      }
    }

    // Check for overpayment if expense assignment is provided
    if (expenseAssignment && !isEdit) {
      const remainingAmount = expenseAssignment.expenseAmount - expenseAssignment.paidAmount;
      if (transactionValue > remainingAmount && remainingAmount > 0) {
        setPendingFormData({ data, transactionValue });
        setShowOverpaymentConfirm(true);
        return;
      }
    }

    await performSave(data, transactionValue);
  };

  const performSave = async (data: FormValues, transactionValue: number) => {
    try {
      if (isEdit) {
        await updateTransaction({
          variables: {
            input: {
              id: transaction.id,
              transactionDate: new Date(data.transactionDate).toISOString(),
              type: data.type,
              value: transactionValue,
              cash: data.cash,
              bankAccountNo: data.cash ? 'cash' : data.bankAccountNo,
              otherAccountNo: data.otherAccountNo || undefined,
              note: data.note || undefined,
              reference: data.reference || undefined,
            },
          },
        });
        show.info('Transakcia bola aktualizovaná.');
      } else {
        const result = await createTransaction({
          variables: {
            input: {
              transactionDate: new Date(data.transactionDate).toISOString(),
              type: data.type,
              value: transactionValue,
              cash: data.cash,
              bankAccountNo: data.cash ? 'cash' : data.bankAccountNo,
              otherAccountNo: data.otherAccountNo || undefined,
              note: data.note || undefined,
              reference: data.reference || undefined,
            },
          },
        });

        // If invoice assignment is configured, create the assignment
        if (invoiceAssignment && result.data?.createMoneyTransaction?.moneyTransaction) {
          const createdTransaction = result.data.createMoneyTransaction.moneyTransaction;
          const remainingAmount = invoiceAssignment.invoiceAmount - invoiceAssignment.paidAmount;

          if (remainingAmount > 0) {
            const assignmentAmount = Math.min(transactionValue, remainingAmount);
            const assignmentResult = await createInvoicePayment({
              variables: {
                input: {
                  transactionId: createdTransaction.id,
                  invoiceId: invoiceAssignment.invoiceId,
                  amount: assignmentAmount,
                },
              },
            });

            if (assignmentResult.data?.createInvoicePayment?.errors?.length) {
              const error = assignmentResult.data.createInvoicePayment.errors[0];
              show.error('Transakcia bola vytvorená, ale nepodarilo sa ju priradiť k faktúre.', error.code);
            } else {
              show.info(
                remainingAmount >= transactionValue
                  ? 'Platba bola úspešne zaznamenaná a priradená k faktúre.'
                  : 'Platba bola úspešne zaznamenaná. Časť sumy bola priradená k faktúre.',
              );
            }
          } else {
            show.info('Transakcia bola vytvorená. Faktúra je už úplne zaplatená.');
          }
        } else if (expenseAssignment && result.data?.createMoneyTransaction?.moneyTransaction) {
          const createdTransaction = result.data.createMoneyTransaction.moneyTransaction;
          const remainingAmount = expenseAssignment.expenseAmount - expenseAssignment.paidAmount;

          if (remainingAmount > 0) {
            const assignmentAmount = Math.min(transactionValue, remainingAmount);
            const assignmentResult = await createExpensePayment({
              variables: {
                input: {
                  transactionId: createdTransaction.id,
                  expenseId: expenseAssignment.expenseId,
                  amount: assignmentAmount,
                },
              },
            });

            if (assignmentResult.data?.createExpensePayment?.errors?.length) {
              const error = assignmentResult.data.createExpensePayment.errors[0];
              show.error('Transakcia bola vytvorená, ale nepodarilo sa ju priradiť k výdavku.', error.code);
            } else {
              show.info(
                remainingAmount >= transactionValue
                  ? 'Platba bola úspešne zaznamenaná a priradená k výdavku.'
                  : 'Platba bola úspešne zaznamenaná. Časť sumy bola priradená k výdavku.',
              );
            }
          } else {
            show.info('Transakcia bola vytvorená. Výdavok je už úplne zaplatený.');
          }
        } else {
          show.info('Transakcia bola vytvorená.');
        }
      }
      onRefresh?.();
      onClose();
    } catch (error: any) {
      show.error('Nepodarilo sa uložiť transakciu.', error.message);
    }
  };

  const handleDelete = async () => {
    if (!transaction) return;

    if (!window.confirm('Naozaj chcete zmazať túto transakciu?')) {
      return;
    }

    try {
      await deleteTransaction({
        variables: {
          id: transaction.id,
        },
      });
      show.info('Transakcia bola zmazaná.');
      onRefresh?.();
      onClose();
    } catch (error: any) {
      show.error('Nepodarilo sa zmazať transakciu.', error.message);
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    setAssignmentToDeleteType('invoice');
    setAssignmentToDelete(assignmentId);
  };

  const handleRemoveExpenseAssignment = async (assignmentId: string) => {
    setAssignmentToDeleteType('expense');
    setAssignmentToDelete(assignmentId);
  };

  const confirmRemoveAssignment = async () => {
    if (!assignmentToDelete) return;

    if (assignmentToDeleteType === 'expense') {
      await deleteExpensePayment({
        variables: {
          id: assignmentToDelete,
        },
      });
    } else {
      await deleteInvoicePayment({
        variables: {
          id: assignmentToDelete,
        },
      });
    }
    setAssignmentToDelete(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {isEdit ? 'Upraviť transakciu' : 'Nová transakcia'}
            <Stack direction="row" spacing={1}>
              {isEdit && (
                <IconButton onClick={handleDelete} disabled={deleting} color="error">
                  <DeleteIcon />
                </IconButton>
              )}
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Dátum transakcie *"
                  type="date"
                  {...register('transactionDate')}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <TextField select label="Typ *" {...field} fullWidth>
                      <MenuItem value="CREDIT">Príjem</MenuItem>
                      <MenuItem value="DEBIT">Výdavok</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Suma *"
                  type="number"
                  {...register('value')}
                  fullWidth
                  inputProps={{
                    step: '0.01',
                  }}
                  helperText={
                    invoiceAssignment && !isEdit
                      ? `Zostáva zaplatiť: ${(invoiceAssignment.invoiceAmount - invoiceAssignment.paidAmount).toFixed(2)} €`
                      : expenseAssignment && !isEdit
                        ? `Zostáva zaplatiť: ${(expenseAssignment.expenseAmount - expenseAssignment.paidAmount).toFixed(2)} €`
                        : undefined
                  }
                  sx={{
                    '& input': {
                      color: parseFloat(watchValue) < 0 ? 'red' : 'inherit',
                    },
                  }}
                />
              </Grid>
              <Controller
                name="cash"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Typ transakcie *</FormLabel>
                    <RadioGroup
                      row
                      value={field.value ? 'cash' : 'bank'}
                      onChange={(e) => field.onChange(e.target.value === 'cash')}
                    >
                      <FormControlLabel value="cash" control={<Radio />} label="Hotovosť" />
                      <FormControlLabel value="bank" control={<Radio />} label="Bankový účet" />
                    </RadioGroup>
                  </FormControl>
                )}
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: watchCash ? 12 : 6 }}>
                  <TextField
                    label="Protiúčet"
                    {...register('otherAccountNo')}
                    fullWidth
                    placeholder="Názov alebo číslo účtu protistrany"
                  />
                </Grid>

                {!watchCash && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Váš účet *"
                      {...register('bankAccountNo')}
                      fullWidth
                      placeholder="XXXX XXXX XXXX XXXX XXXX XXXX"
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Referencia" {...register('reference')} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {isEdit && (
                    <TextField
                      label="Spárovaná suma"
                      value={`${transaction?.reconciledValue ?? 0} €`}
                      fullWidth
                      inputProps={{ readOnly: true }}
                      helperText="Automaticky vypočítané z priradených platieb"
                    />
                  )}
                </Grid>
              </Grid>
              <TextField label="Poznámka" {...register('note')} fullWidth multiline rows={3} />
              {isEdit && transaction?.invoicePayments && transaction.invoicePayments.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Priradené faktúry
                  </Typography>
                  <InvoicePaymentList
                    payments={transaction.invoicePayments}
                    context="transaction"
                    onRemove={handleRemoveAssignment}
                  />
                </Box>
              )}
              {isEdit && transaction?.expensePayments && transaction.expensePayments.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Priradené výdavky
                  </Typography>
                  <ExpensePaymentList
                    payments={transaction.expensePayments}
                    context="transaction"
                    onRemove={handleRemoveExpenseAssignment}
                  />
                </Box>
              )}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {isEdit && transaction && transaction.reconciledValue !== transaction.value && onAssignPayment && (
              <Button
                variant="contained"
                color={transaction.reconciledValue <= 0 ? 'error' : 'info'}
                onClick={onAssignPayment}
              >
                Priraď platbu
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={onClose}>Zrušiť</Button>
            <Button
              onClick={handleSubmit(handleSave)}
              variant="contained"
              disabled={creating || updating || creatingInvoicePayment || creatingExpensePayment}
              sx={{ ml: 1 }}
            >
              {isEdit ? 'Uložiť' : 'Vytvoriť'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for removing assignment */}
      {assignmentToDelete && (
        <ConfirmationDialog
          open={!!assignmentToDelete}
          title="Odstránenie úhrady"
          message="Naozaj chcete odstrániť túto úhradu?"
          onCancel={() => setAssignmentToDelete(null)}
          onConfirm={confirmRemoveAssignment}
          cancelLabel="Zrušiť"
          confirmLabel="Odstrániť"
        />
      )}

      {/* Confirmation dialog for overpayment */}
      {showOverpaymentConfirm && (invoiceAssignment || expenseAssignment) && pendingFormData !== null && (
        <ConfirmationDialog
          open={showOverpaymentConfirm}
          title="Potvrdiť platbu"
          message={(() => {
            const remaining = invoiceAssignment
              ? invoiceAssignment.invoiceAmount - invoiceAssignment.paidAmount
              : expenseAssignment
                ? expenseAssignment.expenseAmount - expenseAssignment.paidAmount
                : 0;
            const label = invoiceAssignment ? 'faktúry' : 'výdavku';
            return `Zadaná suma (${pendingFormData.transactionValue.toFixed(2)} €) je vyššia ako zostávajúca hodnota ${label} (${remaining.toFixed(2)} €). K ${label === 'faktúry' ? 'faktúre' : 'výdavku'} bude priradených len ${remaining.toFixed(2)} € a zvyšok zostane nespárovaný. Pokračovať?`;
          })()}
          onCancel={() => {
            setShowOverpaymentConfirm(false);
            setPendingFormData(null);
          }}
          onConfirm={() => {
            setShowOverpaymentConfirm(false);
            performSave(pendingFormData.data, pendingFormData.transactionValue);
            setPendingFormData(null);
          }}
          cancelLabel="Zrušiť"
          confirmLabel="Pokračovať"
        />
      )}
    </>
  );
}
